import { FileText, Layers, Package, ClipboardCheck, Inbox, Eye, X, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import { OrderDetails, SelectedProduct } from "../../../../../services/api/OrderService/types";
import { OnlineOrderService } from "../../../../../services/api/OrderService/OnlineOrder";
import { useAuth } from "../../../../../context/AuthContext";
import ViewOrderDetails from "../../../../OrderManagement/ViewOrderDetails";
import DispatchProducts from "./DispatchProducts";
import { AdminOrderManagementService } from "../../../../../services/api/admin-order.service";
import { LabService } from "../../../../../services/api/LaborderService/lab.service";
import { Driver } from "../../../../../services/api/LaborderService/types";
import toast from "react-hot-toast";
import OrderPagination from "../../../../OrderManagement/OrderPagination";
import ReturnOrderDetails from "../../../../OrderManagement/ReturnOrderDetails";

type TabType = "pending" | "ongoing" | "completed" | "return";

const TAB_ICONS: Record<TabType, React.ReactNode> = {
    pending: <Layers size={14} />,
    ongoing: <Package size={14} />,
    completed: <ClipboardCheck size={14} />,
    return: <RefreshCw size={14} />,
};

const TAB_LABELS: Record<TabType, string> = {
    pending: "Pending",
    ongoing: "Ongoing",
    completed: "Completed",
    return: "Return orders",
};

const OnlineOrder = () => {
    const { user } = useAuth();
    const [orders, setOrders] = useState<any[]>([]);
    const [selectedOrder, setSelectedOrder] = useState<OrderDetails | null>(null);
    const [activeTab, setActiveTab] = useState<TabType>("pending");
    const [subView, setSubView] = useState<"list" | "view" | "dispatch">("list");
    const [pdfBillUrl, setPdfBillUrl] = useState<string | null>(null);

    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: 10,
        totalPages: 0,
        totalCount: 0,
    });
    const [isLoading, setIsLoading] = useState(false);
    const [refreshCount, setRefreshCount] = useState(0);
    const [selectedProductLists, setSelectedProductLists] = useState<SelectedProduct[]>([]);
    const [deliverPersons, setDeliveryPersons] = useState<Driver[]>([]);
    const [selectedDriver, setSelectedDriver] = useState<string>();
    const [submitting, setSubmitting] = useState(false);

    const clinicId = user?.role === "clinic" ? user?.id : "";

    const onDriverSelect = (driver: Driver) => {
        setSelectedDriver(driver.id);
    };

    const handleProcessOrder = async () => {
        try {
            setSubmitting(true);
            if (!selectedDriver) {
                toast.error("Please select a driver");
                return;
            }
            const orderId = selectedOrder?.orderId;
            if (!orderId) {
                toast.error("Order ID is required");
                return;
            }
            const products = selectedProductLists.map((prod) => {
                return {
                    productId: prod.product_id || prod.product?.id || "",
                    inwardId: prod.id,
                    quantity: prod.selectedQuantity
                };
            });
            const response = await OnlineOrderService.updateOrderByClinic(orderId, selectedDriver, products);
            if (response.success) {
                toast.success("Order dispatched and rider assigned successfully!");
                setSubView("list");
                setSelectedOrder(null);
                fetchOrders();
                fetchAvailableDrivers();
            } else {
                toast.error(response.message || "Failed to process order");
            }
        } catch (error) {
            toast.error(error as string);
        } finally {
            setSubmitting(false);
        }
    };

    const fetchAvailableDrivers = async () => {
        if (!clinicId) return;
        try {
            const response = await LabService.getAvailableDrivers(clinicId, "driver");
            if (response.success) {
                setDeliveryPersons(response.data);
            }
        } catch (error) {
            console.error("Error fetching drivers:", error);
        }
    };

    const mapAdminDetailsToClinicDetails = (admin: any): any => {
        if (!admin) return null;
        return {
            orderId: admin.orderId,
            orderNumber: admin.orderNumber,
            patientId: admin.patientId,
            orderType: admin.orderType,
            orderDate: admin.orderDate,
            orderStatus: admin.orderStatus,
            orderTotal: parseFloat(admin.totalAmount) || 0,
            paymentId: admin.paymentId || "",
            paymentStatus: admin.paymentStatus || (admin.totalAmount ? "success" : "pending"),
            isPrescriptionRequired: admin.isPrescriptionRequired || false,
            hasPrescription: admin.hasPrescription || false,
            orderAddress: admin.address || "",
            orderPincode: admin.pincode || "",
            isLabTest: admin.orderType === "lab_test",
            productDetails: (admin.products || []).map((p: any) => ({
                productId: p.productId?.toString() || "",
                productName: p.name || "",
                productImage: p.productImage || "",
                hsnNumber: p.hsnNumber || "",
                cgst: p.cgst || "0",
                sgst: p.sgst || "0",
                pack: p.pack || 1,
                price: p.productPrice?.toString() || "0",
                mrp: p.productMrp?.toString() || "0",
                totalQuantity: p.quantity || 0,
            })),
            clinicDetails: {
                clinicName: admin.clinicName || "",
                clinicProfileImage: null,
                clinicSupervisorName: "",
                clinicAddress: "",
                clinicPincode: admin.pincode || "",
                clinicMobileNumber: admin.clinicPhone || "",
            },
            paymentDetails: {
                paymentId: admin.paymentId || "",
                PaymentStatus: admin.paymentStatus || "success",
                paymentAmount: parseFloat(admin.totalAmount) || 0,
                paymentMethod: admin.paymentMethod || "Online",
                paymentDate: admin.orderDate,
                paymentFailureReason: null,
            },
            prescriptionDetails: (admin.prescriptions || []).map((rx: any, idx: number) => ({
                id: idx + 1,
                source: rx.source || "PATIENT_UPLOAD",
                prescriptionImage: rx.media || "",
                note: rx.note || "",
            })),
            deliveryPersonDetails: {
                deliveryPersonName: admin.deliveryName || "",
                deliveryPersonProfileImage: null,
                deliveryPersonMobileNumber: admin.deliveryPhone || "",
                deliveryPersonVehicleNumber: null,
            },
            patientDetails: {
                patientName: admin.orderedForName || admin.patientName || "Unknown Patient",
                patientProfileImage: admin.orderForImage || admin.patientImage || "",
                patientMobileNumber: admin.orderedForPhone || admin.patientPhone || "",
                patientAddress: admin.address || "",
                patientPincode: admin.pincode || "",
            },
            invoice: admin.invoice || "",
            clinicId: admin.clinicId || "",
            createdAt: admin.orderDate,
            updatedAt: admin.orderDate,
        };
    };

    const fetchSelectedOrderDetails = async (orderId: number) => {
        try {
            setIsLoading(true);
            const response = await AdminOrderManagementService.getOrderDetails(orderId);
            const mappedOrder = mapAdminDetailsToClinicDetails(response);
            setSelectedOrder(mappedOrder);
        } catch (err) {
            console.error("Error fetching order details:", err);
            toast.error("Failed to load full order details");
        } finally {
            setIsLoading(false);
        }
    };

    const fetchOrders = async () => {
        setIsLoading(true);
        setSelectedDriver(undefined);
        setSelectedProductLists([]);

        const statusMap: Record<TabType, string> = {
            pending: "clinic_assigned",
            ongoing: "Ongoing",
            completed: "Completed",
            return: "all",
        };

        try {
            let response;
            if (activeTab === "return") {
                response = await AdminOrderManagementService.getReturnOrderListing(
                    pagination.page,
                    pagination.pageSize,
                    undefined,
                    clinicId
                );
            } else {
                response = await AdminOrderManagementService.getOrderListing(
                    pagination.page,
                    pagination.pageSize,
                    statusMap[activeTab],
                    clinicId
                );
            }
            if (response && response.success) {
                setOrders(response.data.ordersList || []);
                setPagination({
                    page: response.data?.pagination?.page || 1,
                    pageSize: response.data?.pagination?.pageSize || 10,
                    totalPages: response.data?.pagination?.totalPages || 0,
                    totalCount: response.data?.pagination?.totalCount || 0,
                });
            }
        } catch (error) {
            toast.error('Failed to fetch orders')
        } finally {
            setIsLoading(false);
        }
    };

    const handleTabChange = (tab: TabType) => {
        setActiveTab(tab);
        setPagination(p => ({ ...p, page: 1 }));
        setSubView("list");
        setSelectedOrder(null);
    };

    const getStatusStyle = (status: string) => {
        if (!status) return "text-slate-500 bg-slate-50 border border-slate-200";
        const clean = status.toLowerCase();
        if (clean === "pending" || clean === "clinic_assigned") return "text-amber-600 bg-amber-50 border border-amber-200";
        if (clean === "cancelled") return "text-rose-600 bg-rose-50 border border-rose-200";
        if (clean === "delivered") return "text-emerald-600 bg-emerald-50 border border-emerald-200";
        if (clean === "out_for_delivery") return "text-blue-600 bg-blue-50 border border-blue-200";
        return "text-indigo-600 bg-indigo-50 border border-indigo-200";
    };

    const getHeadersForTab = (tab: TabType) => {
        if (tab === "return") {
            return ["Return ID", "Original Order", "Ordered By", "Products", "Address", "Amount", "Return Status", "Action"];
        }
        if (tab === "pending") {
            return ["Order ID", "Ordered By", "Products", "Delivery Address", "Amount", "Action"];
        }
        if (tab === "ongoing") {
            return ["Order ID", "Ordered By", "Products", "Delivery Address", "Amount", "Status", "Action"];
        }
        return ["Order ID", "Ordered By", "Clinic Details", "Products", "Address", "Amount", "Current Status", "Action", "Bill"];
    };

    useEffect(() => {
        fetchAvailableDrivers();
    }, [clinicId]);

    useEffect(() => {
        fetchOrders();
    }, [activeTab, pagination.page, clinicId, refreshCount]);

    const headers = getHeadersForTab(activeTab);

    return (
        <div className="h-full flex flex-col bg-slate-50">

            <div className="flex-grow flex flex-col h-full overflow-hidden">

                {/* Header bar and subView header fallback */}
                {subView === "list" && (
                    <div className="px-5 pt-3 pb-0 bg-white border-b border-slate-200 flex-shrink-0 shadow-sm z-[5]">
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2.5">
                                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-800 flex items-center justify-center text-white">
                                    <Package size={16} />
                                </div>
                                <h1 className="text-base font-bold text-slate-800">Online Order Management</h1>
                            </div>
                            <button
                                onClick={() => {
                                    setPagination((p) => ({ ...p, page: 1 }));
                                    setRefreshCount((prev) => prev + 1);
                                }}
                                className="p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-500 transition-colors border-none cursor-pointer flex items-center justify-center"
                                title="Refresh List"
                            >
                                <RefreshCw size={14} className={isLoading ? "animate-spin" : ""} />
                            </button>
                        </div>
                        {/* Tabs */}
                        <div className="flex">
                            {(["pending", "ongoing", "completed", "return"] as const).map((tab) => (
                                <button
                                    key={tab}
                                    className={`px-4 py-2.5 text-xs font-semibold relative whitespace-nowrap transition-colors border-none bg-transparent cursor-pointer ${activeTab === tab ? "text-emerald-500 font-bold" : "text-slate-500 hover:text-slate-700"
                                        }`}
                                    onClick={() => handleTabChange(tab)}
                                >
                                    <span className="inline-flex items-center gap-1.5">
                                        {TAB_ICONS[tab]}
                                        {TAB_LABELS[tab]}
                                    </span>
                                    {activeTab === tab && (
                                        <span className="ml-1.5 px-1.5 py-px rounded-full text-[0.6rem] font-bold bg-emerald-50 text-emerald-500">
                                            {pagination.totalCount}
                                        </span>
                                    )}
                                    {activeTab === tab && (
                                        <span className="absolute bottom-0 left-0 right-0 h-[2.5px] bg-gradient-to-r from-emerald-500 to-emerald-500 rounded-t" />
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Dashboard view body */}
                <div className="flex-1 overflow-y-auto p-6 min-h-0">
                    {subView === "list" && (
                        <div className="space-y-4">
                            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden relative shadow-sm">
                                <div className="overflow-x-auto">
                                    <table className="w-full text-[0.8rem] min-w-[900px]">
                                        <thead>
                                            <tr className="bg-slate-50 sticky top-0 z-[2]">
                                                {headers.map((h) => (
                                                    <th
                                                        key={h}
                                                        className="px-3 py-2.5 text-left font-semibold text-slate-500 border-b border-slate-200 whitespace-nowrap uppercase text-[0.65rem] tracking-wider"
                                                    >
                                                        {h}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {isLoading ? (
                                                Array.from({ length: 5 }).map((_, i) => (
                                                    <tr key={i}>
                                                        {headers.map((_, j) => (
                                                            <td key={j} className="px-3 py-3">
                                                                <div
                                                                    className="h-3.5 rounded bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100 animate-pulse"
                                                                    style={{ width: j === 3 ? 160 : 80 }}
                                                                />
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))
                                            ) : orders.length === 0 ? (
                                                <tr>
                                                    <td colSpan={headers.length}>
                                                        <div className="flex flex-col items-center justify-center py-16 text-slate-400 bg-white">
                                                            <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                                                                <Inbox size={24} />
                                                            </div>
                                                            <p className="text-sm font-semibold text-slate-500">
                                                                No {activeTab} orders found
                                                            </p>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : (
                                                orders.map((order: any) => {
                                                    if (activeTab === "return") {
                                                        const statusStyle = getStatusStyle(order.returnStatus);
                                                        const statusLabel = order.returnStatus?.replace(/_/g, " ").toUpperCase() || "—";
                                                        const formattedDate = new Date(order.returnDate || order.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
                                                        const returnTotal = order.returnTotal ?? order.totalAmount;
                                                        const returnItemsCount = order.returnItemsCount ?? order.productCount;

                                                        return (
                                                            <tr key={order.returnId || order.orderId} className="hover:bg-slate-50/80 transition-colors border-b border-slate-50 last:border-b-0">
                                                                {/* Return ID */}
                                                                <td className="px-3 py-2.5">
                                                                    <span className="font-extrabold text-emerald-600 text-xs">#{order.returnId}</span>
                                                                    <div className="text-[0.65rem] text-slate-400 mt-0.5">{formattedDate}</div>
                                                                </td>
                                                                {/* Original Order */}
                                                                <td className="px-3 py-2.5">
                                                                    <span className="font-semibold text-slate-700 text-xs">#{order.orderNumber}</span>
                                                                </td>
                                                                {/* Ordered By */}
                                                                <td className="px-3 py-2.5">
                                                                    <div className="font-semibold text-slate-800 text-xs">{order.patientName}</div>
                                                                    <div className="text-[0.65rem] text-slate-400 mt-0.5">{order.patientPhone}</div>
                                                                </td>
                                                                {/* Products */}
                                                                <td className="px-3 py-2.5">
                                                                    <span className="bg-emerald-50 text-emerald-600 px-2.5 py-1 rounded-full font-bold text-[0.7rem]">
                                                                        {returnItemsCount} item{returnItemsCount !== 1 ? "s" : ""}
                                                                    </span>
                                                                </td>
                                                                {/* Address */}
                                                                <td className="px-3 py-2.5 max-w-[200px]">
                                                                    <div className="truncate text-xs text-slate-600" title={`${order.address}, ${order.city}, ${order.state} - ${order.pincode}`}>
                                                                        {order.address}
                                                                    </div>
                                                                    <div className="text-[0.65rem] text-slate-400 mt-0.5">{order.city}, {order.pincode}</div>
                                                                </td>
                                                                {/* Amount */}
                                                                <td className="px-3 py-2.5">
                                                                    <span className="font-extrabold text-slate-800 text-xs">₹ {returnTotal}</span>
                                                                </td>
                                                                {/* Return Status */}
                                                                <td className="px-3 py-2.5">
                                                                    <span className={`px-2 py-1 rounded-lg font-bold text-[0.62rem] tracking-wide inline-block ${statusStyle}`}>
                                                                        {statusLabel}
                                                                    </span>
                                                                </td>
                                                                {/* Action */}
                                                                <td className="px-3 py-2.5">
                                                                    <button
                                                                        onClick={() => {
                                                                            setSelectedOrder({
                                                                                orderId: order.orderId,
                                                                                orderNumber: order.orderNumber,
                                                                            } as any);
                                                                            setSubView("view");
                                                                        }}
                                                                        className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-[0.7rem] font-bold bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200 transition-all cursor-pointer shadow-sm"
                                                                    >
                                                                        {
                                                                            "View Details"}
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        );
                                                    }

                                                    const statusStyle = getStatusStyle(order.orderStatus);
                                                    const statusLabel = order.orderStatus?.replace(/_/g, " ").toUpperCase() || "—";
                                                    const formattedDate = new Date(order.orderDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

                                                    return (
                                                        <tr key={order.orderId} className="hover:bg-slate-50/80 transition-colors border-b border-slate-50 last:border-b-0">
                                                            {/* Order ID */}
                                                            <td className="px-3 py-2.5">
                                                                <span className="font-extrabold text-emerald-600 text-xs">#{order.orderNumber}</span>
                                                                <div className="text-[0.65rem] text-slate-400 mt-0.5">{formattedDate}</div>
                                                            </td>
                                                            {/* Ordered By */}
                                                            <td className="px-3 py-2.5">
                                                                <div className="font-semibold text-slate-800 text-xs">{order.patientName}</div>
                                                                <div className="text-[0.65rem] text-slate-400 mt-0.5">{order.patientPhone}</div>
                                                            </td>

                                                            {/* Clinic Details (Delivered/Completed tab only) */}
                                                            {activeTab === "completed" && (
                                                                <td className="px-3 py-2.5">
                                                                    <div className="font-semibold text-slate-800 text-xs">{order.clinicName || "—"}</div>
                                                                    <div className="text-[0.65rem] text-slate-400 mt-0.5">{order.clinicPhone || ""}</div>
                                                                </td>
                                                            )}

                                                            {/* Products */}
                                                            <td className="px-3 py-2.5">
                                                                <span className="bg-emerald-50 text-emerald-600 px-2.5 py-1 rounded-full font-bold text-[0.7rem]">
                                                                    {order.productCount} item{order.productCount !== 1 ? "s" : ""}
                                                                </span>
                                                            </td>

                                                            {/* Address */}
                                                            <td className="px-3 py-2.5 max-w-[200px]">
                                                                <div className="truncate text-xs text-slate-600" title={`${order.address}, ${order.city}, ${order.state} - ${order.pincode}`}>
                                                                    {order.address}
                                                                </div>
                                                                <div className="text-[0.65rem] text-slate-400 mt-0.5">{order.city}, {order.pincode}</div>
                                                            </td>

                                                            {/* Amount */}
                                                            <td className="px-3 py-2.5">
                                                                <span className="font-extrabold text-slate-800 text-xs">₹ {order.totalAmount}</span>
                                                            </td>

                                                            {/* Status (Ongoing & Completed tabs) */}
                                                            {activeTab !== "pending" && (
                                                                <td className="px-3 py-2.5">
                                                                    <span className={`px-2 py-1 rounded-lg font-bold text-[0.62rem] tracking-wide inline-block ${statusStyle}`}>
                                                                        {statusLabel}
                                                                    </span>
                                                                </td>
                                                            )}

                                                            {/* Action */}
                                                            <td className="px-3 py-2.5">
                                                                {activeTab === "pending" ? (
                                                                    <button
                                                                        onClick={() => {
                                                                            fetchSelectedOrderDetails(order.orderId);
                                                                            setSubView("dispatch");
                                                                        }}
                                                                        className="inline-flex items-center justify-center gap-2 w-[180px] py-2 rounded-lg text-xs font-semibold cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-md bg-[#e6f7f4] border border-[#00a896] text-[#00a896] hover:bg-[#d1f2ec] hover:text-[#007d70]"
                                                                    >
                                                                        <Package size={14} />
                                                                        Dispatch Products
                                                                    </button>
                                                                ) : (
                                                                    <button
                                                                        onClick={() => {
                                                                            fetchSelectedOrderDetails(order.orderId);
                                                                            setSubView("view");
                                                                        }}
                                                                        className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-[0.7rem] font-bold bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200 transition-all cursor-pointer shadow-sm"
                                                                    >
                                                                        <Eye size={12} />
                                                                        View Details
                                                                    </button>
                                                                )}
                                                            </td>

                                                            {/* Bill (Completed Tab only) */}
                                                            {activeTab === "completed" && (
                                                                <td className="px-3 py-2.5">
                                                                    {order.bill ? (
                                                                        <button
                                                                            onClick={() => setPdfBillUrl(order.bill || null)}
                                                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[0.7rem] font-bold bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-500 hover:text-white transition-all cursor-pointer shadow-sm"
                                                                        >
                                                                            <FileText size={12} />
                                                                            View Bill
                                                                        </button>
                                                                    ) : (
                                                                        <span className="text-slate-350 text-xs font-semibold">Not Generated</span>
                                                                    )}
                                                                </td>
                                                            )}
                                                        </tr>
                                                    );
                                                })
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Pagination */}
                            <OrderPagination
                                page={pagination.page}
                                totalPages={pagination.totalPages}
                                totalCount={pagination.totalCount}
                                onPageChange={(page) => setPagination(p => ({ ...p, page }))}
                            />
                        </div>
                    )}

                    {/* View Sub-View Details */}
                    {subView === "view" && selectedOrder && (
                        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden flex flex-col h-full shadow-sm">
                            {activeTab === "return" ? (
                                <ReturnOrderDetails
                                    orderId={selectedOrder.orderId}
                                    onBack={() => {
                                        setSubView("list");
                                        setSelectedOrder(null);
                                        fetchOrders();
                                    }}
                                    role="clinic"
                                />
                            ) : (
                                <ViewOrderDetails
                                    order={{ orderId: selectedOrder.orderId, orderNumber: selectedOrder.orderNumber }}
                                    onBack={() => setSubView("list")}
                                    hidePrescriptions={true}
                                />
                            )}
                        </div>
                    )}

                    {/* Dispatch Sub-View */}
                    {subView === "dispatch" && selectedOrder && (
                        <div className="overflow-hidden flex flex-col h-full">
                            <DispatchProducts
                                orderId={selectedOrder.orderId}
                                handleDispatchProducts={(val) => {
                                    if (val) setSubView("dispatch");
                                    else setSubView("list");
                                }}
                                selectedProductLists={selectedProductLists}
                                setSelectedProductLists={setSelectedProductLists}
                                handleProcessOrder={handleProcessOrder}
                                drivers={deliverPersons}
                                selectedDriver={selectedDriver}
                                onDriverSelect={onDriverSelect}
                                submitting={submitting}
                                clinicId={clinicId}
                            />
                        </div>
                    )}
                </div>
            </div>


            {/* Bill Preview PDF Modal */}
            {pdfBillUrl && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[200000] flex items-center justify-center p-4 animate-fadeIn" onClick={() => setPdfBillUrl(null)}>
                    <div className="bg-white rounded-2xl w-full max-w-4xl h-[85vh] flex flex-col shadow-2xl overflow-hidden animate-slideUp" onClick={(e) => e.stopPropagation()}>
                        {/* Header */}
                        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-white">
                            <h3 className="text-sm font-bold text-slate-800">Order Invoice / Bill</h3>
                            <button
                                className="w-8 h-8 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center hover:bg-red-50 hover:text-red-500 transition-colors border-none cursor-pointer"
                                onClick={() => setPdfBillUrl(null)}
                            >
                                <X size={16} />
                            </button>
                        </div>
                        {/* Body */}
                        <div className="flex-grow bg-slate-100">
                            <iframe
                                src={`${pdfBillUrl}#toolbar=0`}
                                className="w-full h-full border-none"
                                title="Invoice Bill PDF"
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OnlineOrder;